/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/EllipsisSlot.tsx
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-ELLIPSIS-001
 * PURPOSE: Reserved always-rendered ellipsis trigger so touch users can open lane menus.
 * ROLE IN ARCHITECTURE: Presentational leaf inside every lane shell.
 * MAIN EXPORTS: EllipsisSlot.
 * NON-RESPONSIBILITIES: No menu state; the routing hook owns open/close.
 * NOTES FOR FUTURE AI: The slot is ALWAYS in the layout (opacity-only rest
 *   state: invisible on fine pointers until lane hover/focus; 100% coarse) —
 *   hiding it with display/width causes layout shift and kills touch
 *   discoverability (plan glitch list). Change #427 quieted rest opacity so
 *   four meatballs stop competing with chips.
 */
import { MoreHorizontal } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'

export interface EllipsisSlotProps {
  label: string
  onOpen: (trigger: HTMLElement) => void
  className?: string
}

/**
 * Renders the reserved lane-menu trigger.
 *
 * Inputs: accessible label and open callback receiving the trigger element
 * (used as the menu anchor + focus-restore target).
 * Output: a 24px-square button on fine pointers (44px coarse); no layout shift
 * between rest/hover states.
 * Failure modes: none — clicking always delegates to the routing owner.
 */
export function EllipsisSlot({ label, onOpen, className }: EllipsisSlotProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-testid="lane-ellipsis"
      onClick={(event) => {
        event.stopPropagation()
        onOpen(event.currentTarget)
      }}
      className={cn(
        // Reserved square slot: opacity animates, geometry never changes on
        // fine pointers; coarse pointers get real 44px growth (item 6 — this
        // slot lives in an auto-height lane, not a fixed band).
        //
        // 24px, not 28 (change #294): the slot now sits inline in a one-row
        // lane, so its box is what sets the lane's minimum height on fine
        // pointers. Coarse growth is untouched — thumbs still get 44px.
        'inline-flex h-6 w-6 shrink-0 touch-manipulation items-center justify-center rounded-md',
        'pointer-coarse:h-11 pointer-coarse:w-11',
        'text-muted-foreground hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        // Quiet at rest on fine pointers (four meatballs were noise); full
        // opacity on lane hover/focus, button hover/focus, or coarse pointer.
        'opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100',
        'group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100',
        className,
      )}
    >
      <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
    </button>
  )
}
